/**
 * Client-side AES-256-GCM encryption utilities using WebCrypto API.
 * Mirrors the Node.js crypto flow in the API's encryption-e2e.test.ts.
 *
 * Key hierarchy:
 *   password → PBKDF2 → passwordKey → wraps/unwraps → KEK → encrypts/decrypts data
 */

const PBKDF2_ITERATIONS = 200_000;
const AES_KEY_BITS = 256;
const GCM_NONCE_BYTES = 12;
const GCM_TAG_BYTES = 16;

// ── Key Derivation ──────────────────────────────────────────────────

/**
 * Derive a 256-bit AES-GCM key from a password + salt via PBKDF2.
 */
export async function derivePasswordKey(
    password: string,
    salt: Uint8Array
): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const baseKey = await crypto.subtle.importKey(
        'raw',
        enc.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: PBKDF2_ITERATIONS,
            hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: AES_KEY_BITS },
        true, // extractable for wrapping
        ['wrapKey', 'unwrapKey']
    );
}

// ── KEK Generation ──────────────────────────────────────────────────

/**
 * Generate a random 256-bit AES-GCM key (KEK).
 */
export async function generateKek(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
        { name: 'AES-GCM', length: AES_KEY_BITS },
        true, // extractable so we can wrap it
        ['encrypt', 'decrypt']
    );
}

// ── KEK Wrapping / Unwrapping ───────────────────────────────────────

/**
 * Wrap (encrypt) a KEK using a password-derived key.
 * Returns base64-encoded nonce and ciphertext (ciphertext includes auth tag).
 */
export async function wrapKek(
    kek: CryptoKey,
    passwordKey: CryptoKey
): Promise<{ nonce: string; ciphertext: string }> {
    const nonce = crypto.getRandomValues(new Uint8Array(GCM_NONCE_BYTES));
    const wrapped = await crypto.subtle.wrapKey(
        'raw',
        kek,
        passwordKey,
        { name: 'AES-GCM', iv: nonce }
    );
    return {
        nonce: uint8ToBase64(nonce),
        ciphertext: uint8ToBase64(new Uint8Array(wrapped)),
    };
}

/**
 * Unwrap (decrypt) a KEK using a password-derived key.
 */
export async function unwrapKek(
    wrappedNonce: string,
    wrappedCiphertext: string,
    passwordKey: CryptoKey
): Promise<CryptoKey> {
    const nonce = base64ToUint8(wrappedNonce);
    const wrapped = base64ToUint8(wrappedCiphertext);
    return crypto.subtle.unwrapKey(
        'raw',
        wrapped,
        passwordKey,
        { name: 'AES-GCM', iv: nonce },
        { name: 'AES-GCM', length: AES_KEY_BITS },
        true,
        ['encrypt', 'decrypt']
    );
}

// ── Blob Encryption / Decryption ────────────────────────────────────

/**
 * Encrypt an ArrayBuffer using AES-256-GCM with a KEK.
 * Returns the encrypted data (including auth tag appended) and the nonce.
 */
export async function encryptBlob(
    data: ArrayBuffer,
    kek: CryptoKey
): Promise<{ encrypted: ArrayBuffer; nonce: string }> {
    const nonce = crypto.getRandomValues(new Uint8Array(GCM_NONCE_BYTES));
    const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce },
        kek,
        data
    );
    return {
        encrypted,
        nonce: uint8ToBase64(nonce),
    };
}

/**
 * Decrypt an AES-256-GCM encrypted ArrayBuffer using a KEK.
 */
export async function decryptBlob(
    encrypted: ArrayBuffer,
    nonceB64: string,
    kek: CryptoKey
): Promise<ArrayBuffer> {
    const nonce = base64ToUint8(nonceB64);
    return crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonce },
        kek,
        encrypted
    );
}

// ── Base64 Helpers ──────────────────────────────────────────────────

export function uint8ToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export function base64ToUint8(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Export a CryptoKey as raw bytes (Uint8Array).
 */
export async function exportKeyRaw(key: CryptoKey): Promise<Uint8Array> {
    const raw = await crypto.subtle.exportKey('raw', key);
    return new Uint8Array(raw);
}
